import { type RefObject, useEffect } from "react";

/**
 * The heights of the banners floating at the bottom of the screen, as CSS
 * variables on the document root while each shows and unset otherwise: the
 * Sticky bottom bar's placement, and the Slide-in's card with its inset.
 * What floats at the bottom of the screen outside the page's flow (the
 * onboarding button, the staff and developer buttons) adds them to its own
 * offset, so neither banner covers it.
 */
export const BOTTOM_BAR_INSET = "--dashboard-bottom-bar";
export const SLIDE_IN_INSET = "--dashboard-slide-in";

/** A `bottom` of `px` pixels, lifted above the bottom banners while any shows. */
export const aboveBottomBanners = (px: number) =>
  `calc(${px}px + var(${BOTTOM_BAR_INSET}, 0px) + var(${SLIDE_IN_INSET}, 0px))`;

/** Keeps `variable` at the element's height while it is mounted. */
export const usePublishedHeight = (
  element: RefObject<HTMLElement | null>,
  variable: string
) => {
  useEffect(() => {
    const published = element.current;
    if (!published) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(variable, `${published.offsetHeight}px`);
    publish();
    // jsdom has no ResizeObserver; there the height is published once.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(publish);
    observer?.observe(published);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(variable);
    };
  }, [element, variable]);
};
